import type { Document, DocumentType } from '@/types';

// ============================================================================
// HIPAA signature — the ONE source of truth (s36)
// ============================================================================
//
// "Is this client's HIPAA signed?" used to be answered by two independent
// fields that nothing kept in agreement:
//   - client.signatureStatus   (written at create time, read by the header
//                                chip + the server-side send gate)
//   - doc.signatureStatus      (written only by the post-creation upload path,
//                                read by the #1243 banner + #1242's replace)
// The create-time flow never stamped the DOCUMENT, so the "signed" banner state
// was unreachable for every normally-created client (finding s35-1), and the
// per-client counters drifted on replace (s35-3).
//
// s36 collapses this: the DOCUMENT is authoritative for its own signature
// state, and the client-level fields become explicitly-derived denormalizations
// (caches for cheap reads/queries — never a parallel truth). This module holds
// the shared vocabulary and the single resolver both sides go through.
//
// -------------------- Vocabulary reconciliation (s36) --------------------
// The two layers use different spellings for the "present but not signed"
// state. We keep both spellings (each is load-bearing in its own layer's UI
// copy and query filters) and reconcile them here, in one place, once:
//
//   meaning                              | document field | client field
//   -------------------------------------|----------------|--------------
//   signature confidently detected       | 'signed'       | 'signed'
//   authorization present, not confirmed | 'unsigned'     | 'pending'
//   signature requested, awaiting return | 'pending'      | 'pending'
//   no authorization at all              | (no doc)       | 'none' / absent
//
// Document 'pending' is the signature-REQUEST-flow state (a form was sent out
// for signing); it maps back to the client's 'pending'. The create/upload
// paths only ever produce 'signed' or 'unsigned'.
// ============================================================================

/** Document-level signature state. The document is authoritative for this. */
export type DocSignatureStatus = 'signed' | 'unsigned' | 'pending';

/** Client-level signature state — a derived denormalization of the docs. */
export type ClientSignatureStatus = 'none' | 'pending' | 'signed';

/**
 * Map a client-level signature signal to the value to stamp on a HIPAA
 * document at create time. Only an explicit 'signed' becomes a signed
 * document; everything else (pending / none / undefined) is 'unsigned' — a
 * missing signal must never manufacture a confident "signed" claim (s36 target
 * model, point 5). Mirrors the create-time gate that decides
 * client.signatureStatus in UploadSignedHipaaFlow.
 */
export function clientSignatureToDocSignature(
  clientStatus: ClientSignatureStatus | undefined,
): DocSignatureStatus {
  return clientStatus === 'signed' ? 'signed' : 'unsigned';
}

/**
 * Map a document-level signature state back to the client-level denormalization
 * (used by the single writer that maintains client.signatureStatus). A signed
 * document → 'signed'; anything else → 'pending' (authorization present but not
 * confirmed). "No document" is handled by the caller as 'none'.
 */
export function docSignatureToClientSignature(
  docStatus: DocSignatureStatus | undefined,
): ClientSignatureStatus {
  return docStatus === 'signed' ? 'signed' : 'pending';
}

// ---------------------------------------------------------------------------
// The single read path (s36 target model, points 3-5)
// ---------------------------------------------------------------------------

/** General-HIPAA document types (client-level authorization, not provider-specific). */
const GENERAL_HIPAA_TYPES: DocumentType[] = [
  'hipaa_auth',
  'hipaa_auth_general',
  'hipaa_auth_signed',
];

/**
 * Signature state of a client's GOVERNING general authorization:
 *  - 'none'     : no active general authorization at all.
 *  - 'signed'   : the governing authorization is confidently signed.
 *  - 'unsigned' : an authorization is present but not signature-confirmed.
 *  - 'unknown'  : an active general authorization exists but its signatureStatus
 *                 is missing/unrecognized — a DATA ERROR. After the s36
 *                 create-time stamp + back-fill this is unreachable, so callers
 *                 must render an honest "couldn't determine" state and log
 *                 loudly (PHI-redacted), NEVER a confident claim.
 */
export type GoverningSignatureStatus = 'none' | 'signed' | 'unsigned' | 'unknown';

export interface GoverningAuthorizationResult {
  /** Active (non-replaced) general HIPAA documents. */
  activeGeneral: Document[];
  /** Replaced general HIPAA documents (retained audit trail), newest first. */
  replacedGeneral: Document[];
  /** The governing authorization: a signed active general if one exists, else the most-recent active general, else null. */
  governing: Document | null;
  /** Resolved signature state of the governing authorization. */
  status: GoverningSignatureStatus;
}

/** Firestore Timestamp | plain — millis for "most recent" comparison, no external import. */
function timestampMillis(value: unknown): number {
  const ts = value as { toMillis?: () => number; seconds?: number } | null | undefined;
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function docRecencyMillis(d: Document): number {
  return timestampMillis(d.uploadedAt ?? d.createdAt);
}

/** When a replaced copy stopped governing (F-12): replaced, else uploaded, else created. */
function replacedRecencyMillis(d: Document): number {
  return timestampMillis(d.replacedAt ?? d.uploadedAt ?? d.createdAt);
}

/**
 * THE shared resolver (s36). Given a client's HIPAA documents, classify the
 * general authorizations and resolve the single governing signature state.
 *
 * This is the one place "is this client's HIPAA signed?" is answered from the
 * documents. The #1243 banner calls it on the docs it already loads; the
 * client-level writer (PR3) calls it to compute the denormalized cache that the
 * header chip and the server-side send gate read. Same logic → the surfaces
 * cannot disagree (the class of bug that produced s35-1 / s35-3).
 *
 * Under Option A there is at most one active general authorization; the resolver
 * is defensive against a malformed multi-active state (prefers a signed one,
 * else the most-recent) but never invents a confident answer for a doc whose
 * signatureStatus is absent — that surfaces as 'unknown'.
 */
export function resolveGoverningAuthorization(
  documents: Document[],
): GoverningAuthorizationResult {
  const general = documents.filter(
    (d) => !d.hipaaSourceCandidate && !d.providerSpecific && GENERAL_HIPAA_TYPES.includes(d.documentType),
  );
  const activeGeneral = general.filter((d) => d.protectedDocumentStatus !== 'replaced');
  // Newest first, so the history reads in date order (F-12).
  const replacedGeneral = general
    .filter((d) => d.protectedDocumentStatus === 'replaced')
    .sort((a, b) => replacedRecencyMillis(b) - replacedRecencyMillis(a));

  if (activeGeneral.length === 0) {
    return { activeGeneral, replacedGeneral, governing: null, status: 'none' };
  }

  // Prefer a confidently-signed active general (green stays reachable even in a
  // malformed multi-active state); otherwise the most-recent active general.
  const signed = activeGeneral.find((d) => d.signatureStatus === 'signed');
  const governing =
    signed ??
    [...activeGeneral].sort((a, b) => docRecencyMillis(b) - docRecencyMillis(a))[0];

  let status: GoverningSignatureStatus;
  if (governing.signatureStatus === 'signed') {
    status = 'signed';
  } else if (governing.signatureStatus === 'unsigned' || governing.signatureStatus === 'pending') {
    status = 'unsigned';
  } else {
    status = 'unknown'; // missing/unrecognized signatureStatus — data error
  }

  return { activeGeneral, replacedGeneral, governing, status };
}

/**
 * Derive the client-level `signatureStatus` denormalization from a client's HIPAA
 * documents (the cache the header chip + server send gate read). The document is
 * authoritative; this is its persisted projection. 'unknown' maps to 'pending'
 * (honest, non-confident) so a data error never clears the send gate.
 */
export function deriveClientSignatureStatus(documents: Document[]): ClientSignatureStatus {
  const { status } = resolveGoverningAuthorization(documents);
  switch (status) {
    case 'signed':
      return 'signed';
    case 'none':
      return 'none';
    case 'unsigned':
    case 'unknown':
    default:
      return 'pending';
  }
}

/**
 * Document types that count as a user-managed HIPAA "form" for the client header
 * chip. This deliberately EXCLUDES `hipaa_auth_provider_signed` — the
 * auto-generated per-facility overlay derivatives, which are internal plumbing
 * regenerated from the base authorization and were never counted by the legacy
 * increment. Manual provider-specific uploads are `hipaa_auth_provider`.
 */
const COUNTABLE_HIPAA_TYPES: DocumentType[] = [
  'hipaa_auth',
  'hipaa_auth_general',
  'hipaa_auth_signed',
  'hipaa_auth_provider',
];

/** The client-level HIPAA denormalizations — derived caches, never parallel truths. */
export interface ClientHipaaDenormalization {
  signatureStatus: ClientSignatureStatus;
  hipaaFormsCount: number;
  hasProviderSpecificHipaa: boolean;
}

/**
 * THE single definition (s36) of the client-level HIPAA denormalizations, derived
 * from the client's documents. One place computes all three so the header chip,
 * the server send gate, and the modal can't drift (s35-1 / s35-3). All three
 * exclude REPLACED documents — under Option A a client has one governing
 * authorization plus zero-or-more replaced copies, and the caches reflect only
 * what governs now:
 *   - signatureStatus         : the governing general authorization's state.
 *   - hipaaFormsCount         : count of NON-REPLACED countable forms — never an
 *                               unbounded increment, so a replace can't inflate it
 *                               (the s35-3 fix). Excludes overlay derivatives.
 *   - hasProviderSpecificHipaa: any non-replaced manual provider-specific upload.
 */
export function deriveClientHipaaFields(documents: Document[]): ClientHipaaDenormalization {
  const active = documents.filter((d) => !d.hipaaSourceCandidate && d.protectedDocumentStatus !== 'replaced');
  return {
    signatureStatus: deriveClientSignatureStatus(documents),
    hipaaFormsCount: active.filter((d) => COUNTABLE_HIPAA_TYPES.includes(d.documentType)).length,
    hasProviderSpecificHipaa: active.some((d) => d.documentType === 'hipaa_auth_provider'),
  };
}
